import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
 Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell,
 TableBody, Paper, CircularProgress, List, ListItem, ListItemText, Divider,
 Button, Alert, Card, CardContent, Stack
} from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import config from './config.js';
import { isTokenExpired } from './auth';
import api from './api';

const bodyFontSize = '1.1rem';

function Report() {
 const { surveyId, userId } = useParams();
 const navigate = useNavigate();
 const [reportData, setReportData] = useState(null); // Holds the combined data for the specific report
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [geminiFeedback, setGeminiFeedback] = useState('');
 const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
 const [feedbackError, setFeedbackError] = useState(null);

 useEffect(() => {
    if (isTokenExpired()) {
        localStorage.clear();
        navigate("/login");
        return;
    }
    const fetchReportData = async () => {
        setLoading(true); setError(null);
        setGeminiFeedback(''); setIsLoadingFeedback(false); setFeedbackError(null);
        try {
            if (!surveyId || !userId) { throw new Error("Survey ID or User ID is missing."); }
            const url = `${config.backendUrl}/report/${surveyId}/${userId}`;
            console.log(`Report.js: Fetching specific report data from ${url}`);
            const response = await api.get(url);
            const data = response.data;
            console.log("Report.js: Fetched Report Data:", JSON.stringify(data, null, 2));

            // Validate the specific structure { user_info: {...}, rounds: [{...}] }
            if (
                data && typeof data === 'object' && !Array.isArray(data) &&
                data.user_info && typeof data.user_info === 'object' &&
                Array.isArray(data.rounds) && data.rounds.length > 0 &&
                data.rounds[0] && typeof data.rounds[0] === 'object' &&
                data.rounds[0].round_info && typeof data.rounds[0].round_info === 'object'
            ) {
                const roundData = data.rounds[0]; // Get the first (and only) round's data

                // **FIX:** Ensure team_quantitative_data is correctly extracted from roundData
                // The backend sends team_quantitative_data within the round object.
                const teamQuantData = (roundData.team_quantitative_data && typeof roundData.team_quantitative_data === 'object')
                                        ? roundData.team_quantitative_data
                                        : {}; // Default to empty object if missing/invalid

                // Combine user_info and the single round's data correctly into one object for the state
                const combinedData = {
                    // User Info
                    user_id: data.user_info.user_id,
                    name: data.user_info.name,
                    team_id: data.user_info.team_id,
                    team_name: data.user_info.team_name,
                    cohort_id: data.user_info.cohort_id,
                    cohort_name: data.user_info.cohort_name,
                    // Round Info
                    survey_id: roundData.round_info.survey_id,
                    survey_name: roundData.round_info.survey_name,
                    start_date: roundData.round_info.start_date,
                    end_date: roundData.round_info.end_date,
                    // Round Data
                    quantitative_data: Array.isArray(roundData.quantitative) ? roundData.quantitative : [],
                    qualitative_feedback: Array.isArray(roundData.qualitative) ? roundData.qualitative : [],
                    multiple_choice_counts: Array.isArray(roundData.mc_feedback) ? roundData.mc_feedback : [],
                    // **FIX:** Assign the extracted teamQuantData here
                    team_quantitative_data: teamQuantData
                };
                console.log("Report.js: Combined data for state:", JSON.stringify(combinedData, null, 2));
                setReportData(combinedData); // Set the combined object as the state
            } else {
                let reason = "Unknown structure issue.";
                if (!data) reason = "API response data is null or undefined.";
                else if (typeof data !== 'object' || Array.isArray(data)) reason = "API response is not an object.";
                else if (!data.user_info) reason = "Missing 'user_info' object.";
                else if (!Array.isArray(data.rounds) || data.rounds.length === 0) reason = "Missing or empty 'rounds' array.";
                else if (!data.rounds[0] || typeof data.rounds[0] !== 'object') reason = "First element in 'rounds' is invalid.";
                else if (!data.rounds[0].round_info) reason = "Missing 'round_info' in the first round.";
                console.error(`Report.js: Unexpected response format or missing required data. Reason: ${reason}`, data);
                setReportData(null);
                setError(new Error("No report data found or invalid format for this specific report."));
            }
        } catch (err) {
            console.error("Report.js: Error fetching report data:", err);
            let errorMsg = "Failed to load report data.";
            if (err.response) { errorMsg = `Error: ${err.response.status} ${err.response.data?.message || err.response.statusText}`; if (err.response.status === 401 || err.response.status === 403) { localStorage.clear(); navigate('/login'); } }
            else if (err.request) { errorMsg = "Network error. Could not reach server."; }
            else { errorMsg = err.message || errorMsg; }
            setError(new Error(errorMsg));
        } finally {
            setLoading(false);
        }
    };
    fetchReportData();
 }, [surveyId, userId, navigate]);

 // --- Group Qualitative Feedback (Unchanged) ---
 const processQualitativeFeedback = (feedback) => {
    if (!Array.isArray(feedback)) return {};
    return feedback.reduce((acc, item) => {
        if (!item || item.question_id == null) return acc;
        const key = item.question_id;
        if (!acc[key]) acc[key] = { question_text: item.question_text || `Question ID: ${key}`, feedback_list: [] };
        if (Array.isArray(item.answers)) {
            item.answers.forEach(answerText => {
                if (answerText && String(answerText).trim() !== "") {
                    const sentimentValue = item.sentiment;
                    const sentimentText = typeof sentimentValue === 'number' ? ` (Sentiment: ${sentimentValue.toFixed(2)})` : '';
                    acc[key].feedback_list.push(`${answerText}${sentimentText}`);
                }
            });
        }
        return acc;
    }, {});
 };
 const groupedQualitativeFeedbackMemo = useMemo(() => processQualitativeFeedback(reportData?.qualitative_feedback), [reportData?.qualitative_feedback]);

 // --- Function to Fetch Gemini Feedback (Unchanged) ---
 const fetchGeminiFeedback = async () => {
     const numUserId = parseInt(userId, 10);
    const numSurveyId = parseInt(surveyId, 10);
    if (isNaN(numUserId) || isNaN(numSurveyId)) { setFeedbackError("Invalid User or Survey ID."); return; }
    console.log(`Report.js: Requesting AI feedback for user: ${numUserId}, survey: ${numSurveyId}`);
    setIsLoadingFeedback(true); setFeedbackError(null); setGeminiFeedback('');
    try {
        const url = `${config.backendUrl}/generate-feedback/${numUserId}/${numSurveyId}`;
        const response = await api.get(url);
        if (response.data && response.data.feedback) { setGeminiFeedback(response.data.feedback); }
        else { throw new Error(response.data?.error || response.data?.message || "Invalid feedback format."); }
    } catch (err) {
        console.error("Report.js: Error fetching Gemini feedback:", err);
        let errorMsg = "Failed to load AI feedback.";
        if (err.response) { errorMsg = `Error: ${err.response.status} ${err.response.data?.error || err.response.data?.message || err.response.statusText}`; }
        else if (err.request) { errorMsg = "Network error connecting to AI feedback service."; }
        else { errorMsg = err.message || errorMsg; }
        setFeedbackError(errorMsg);
    } finally { setIsLoadingFeedback(false); }
 };


 // --- Render Logic ---
 if (loading) { return ( <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}> <CircularProgress /> </Box> ); }
 if (error) { return ( <Box sx={{ textAlign: "center", mt: 4 }}> <Alert severity="error">{error.message}</Alert> <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}> Retry </Button> </Box> ); }
 if (!reportData || reportData.user_id == null) {
    console.log("Report.js: Render check failed. reportData:", reportData);
    return ( <Box sx={{ textAlign: "center", mt: 4 }}> <Typography variant="h6">No report data available for this user and survey.</Typography> </Box> );
 }

 // Access team data directly from reportData state
 const teamData = reportData?.team_quantitative_data ?? {};
 console.log("Report.js: Using teamData for rendering:", JSON.stringify(teamData, null, 2));

 // --- Render Report ---
 return (
  <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
   <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 3 }}> Report Details </Typography>
   <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
       <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}> Participant Report </Typography>
       <Typography sx={{ fontSize: bodyFontSize }}> Participant Name: {reportData?.name ?? 'N/A'} </Typography>
       <Typography sx={{ fontSize: bodyFontSize }}> Survey: {reportData?.survey_name ?? 'N/A'} </Typography>
       <Typography sx={{ fontSize: bodyFontSize }}> Team: {reportData?.team_name ?? 'N/A'} </Typography>
       <Typography sx={{ fontSize: bodyFontSize }}> Cohort: {reportData?.cohort_name ?? 'N/A'} </Typography>
   </Paper>

   {/* AI Feedback Section */}
    <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 1 }}>
                 <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}> AI-Generated Summary </Typography>
                 <Button variant="contained" color="secondary" startIcon={isLoadingFeedback ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />} onClick={fetchGeminiFeedback} disabled={isLoadingFeedback || !reportData} sx={{ width: { xs: '100%', sm: 'auto' } }} > {isLoadingFeedback ? 'Generating...' : (geminiFeedback ? 'Regenerate' : 'Generate Summary')} </Button>
            </Stack>
             <Typography variant="caption" sx={{ display: 'block', textAlign: 'left', mb: 2, color: 'text.secondary' }}> Powered by Google Gemini. AI can make mistakes, so use your critical thinking skills! </Typography>
             <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, minHeight: '100px' }}>
                 {isLoadingFeedback && ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}> <CircularProgress size={30} /> <Typography sx={{ ml: 2 }}>Generating feedback...</Typography> </Box> )}
                 {feedbackError && !isLoadingFeedback && ( <Alert severity="error" onClose={() => setFeedbackError(null)}> {feedbackError} </Alert> )}
                 {geminiFeedback && !isLoadingFeedback && !feedbackError && ( <Typography component="div" sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', textAlign: 'left', fontSize: bodyFontSize }}> {geminiFeedback} </Typography> )}
                 {!geminiFeedback && !isLoadingFeedback && !feedbackError && ( <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center', pt: 3 }}> Click "Generate Summary" to get AI insights. </Typography> )}
             </Box>
        </CardContent>
    </Card>

   {/* Quantitative Data Table */}
   <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, textAlign: 'left', fontWeight: 'bold' }}> Quantitative Feedback </Typography>
   <TableContainer component={Paper} sx={{ mb: 4 }}>
       <Table>
           <TableHead> <TableRow> <TableCell align="left">Question</TableCell> <TableCell align="right">Your Avg Score</TableCell> <TableCell align="right">Team Avg Score</TableCell> <TableCell align="left">Feedback</TableCell> </TableRow> </TableHead>
           <TableBody>
               {reportData.quantitative_data && Array.isArray(reportData.quantitative_data) && reportData.quantitative_data.length > 0 ? (
                   reportData.quantitative_data.map((item, index) => {
                       if (!item || item.question_id === undefined || item.question_id === null) {
                           console.warn("Report.js: Skipping invalid quantitative item:", item);
                           return null;
                       }
                       // **FIX:** Access team_average directly from the item object
                       // const teamAvgScore = item.team_average; // No longer needed
                       return (
                           <TableRow key={item.question_id || index}>
                               <TableCell align="left" sx={{ fontSize: bodyFontSize }}> {item.question_text ?? "Unknown Question"} </TableCell>
                               <TableCell align="right" sx={{ fontSize: bodyFontSize }}> {item.peer_average ?? "N/A"} </TableCell>
                               {/* **FIX:** Display item.team_average directly */}
                               <TableCell align="right" sx={{ fontSize: bodyFontSize }}> {item.team_average != null ? item.team_average : "N/A"} </TableCell>
                               <TableCell align="left" sx={{ fontSize: bodyFontSize }}> {item.feedback ?? "No feedback provided."} </TableCell>
                           </TableRow>
                       );
                   })
               ) : ( <TableRow> <TableCell colSpan={4} align="center">No quantitative data available.</TableCell> </TableRow> )}
           </TableBody>
       </Table>
   </TableContainer>

   {/* Grouped Qualitative Feedback */}
   {Object.keys(groupedQualitativeFeedbackMemo).length > 0 && (
       <Box sx={{ mt: 4 }}>
           <Typography variant="h5" gutterBottom sx={{ mb: 2, textAlign: 'left', fontWeight: 'bold' }}> Qualitative Feedback </Typography>
           <Paper elevation={1} sx={{ p: 2 }}>
               {Object.entries(groupedQualitativeFeedbackMemo).map(([questionId, groupData], index) => (
                   <Box key={questionId} sx={{ mb: groupData.feedback_list.length > 0 ? 3 : 0 }}>
                       <Typography variant="h6" gutterBottom sx={{ fontWeight: 'normal', textAlign: 'left' }}> {groupData.question_text} </Typography>
                       {groupData.feedback_list.length > 0 ? (
                           <List dense disablePadding>
                               {groupData.feedback_list.map((feedback, feedbackIndex) => (
                                   <React.Fragment key={feedbackIndex}>
                                       <ListItem sx={{ pl: 2 }}>
                                           <ListItemText primary={feedback} primaryTypographyProps={{ sx: { fontSize: bodyFontSize } }} />
                                       </ListItem>
                                       {feedbackIndex < groupData.feedback_list.length - 1 && <Divider component="li" variant="inset" />}
                                   </React.Fragment>
                               ))}
                           </List>
                       ) : (
                            <Typography sx={{ pl: 2, fontStyle: 'italic', color: 'text.secondary' }}>No feedback provided for this question.</Typography>
                       )}
                       {index < Object.keys(groupedQualitativeFeedbackMemo).length - 1 && <Divider sx={{ my: 2 }} />}
                   </Box>
               ))}
           </Paper>
       </Box>
   )}

   {/* Multiple Choice Counts & Feedback */}
   {reportData.multiple_choice_counts && Array.isArray(reportData.multiple_choice_counts) && reportData.multiple_choice_counts.length > 0 && (
    <Box sx={{ mt: 4 }}>
         <Typography variant="h5" gutterBottom sx={{ mb: 2, textAlign: 'left', fontWeight: 'bold' }}> Multiple Choice Results </Typography>
         <Paper elevation={1} sx={{ p: 2 }}>
            {reportData.multiple_choice_counts.map((questionData, index) => {
                if (!questionData || questionData.question_id == null) {
                    console.warn("Report.js: Skipping invalid MC item:", questionData);
                    return null;
                }
                const questionId = questionData.question_id;
                const summaryText = questionData.feedback_summary || "No responses for this question.";
                return (
                    <Box key={questionId} sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'normal', textAlign: 'left' }}> {questionData.question_text || `Question ID: ${questionId}`} </Typography>
                        <Typography sx={{ pl: 2, mb: 1, fontSize: bodyFontSize, whiteSpace: 'pre-wrap' }}>
                             {summaryText.split(', ').map((part, i) => <React.Fragment key={i}>{part}<br/></React.Fragment>)}
                        </Typography>
                        {(questionData.weighted_contribution_score != null) && ( <Typography variant="body2" sx={{ pl: 2, textAlign: 'left', mt: 0.5, fontSize: bodyFontSize }}> <strong>Your Individual Contribution Score This Round:</strong> {questionData.weighted_contribution_score} </Typography> )}
                        {(questionData.team_highest_contribution_score != null) && ( <Typography variant="body2" sx={{ pl: 2, textAlign: 'left', mb: 1.5, fontSize: bodyFontSize }}> <strong>Your Team's Highest Contribution Score This Round:</strong> {questionData.team_highest_contribution_score} </Typography> )}
                        {questionData.feedback_current && ( <Alert severity="info" variant="outlined" icon={false} sx={{ mt: 1, mb: 1, py: 1, textAlign: 'left', '& .MuiAlert-message': { py: 0, width: '100%' } }}> <Box sx={{ display: 'flex', alignItems: 'flex-start' }}> <Typography sx={{ fontSize: bodyFontSize, mr: 1, flexShrink: 0 }}> <strong>This Round:</strong> </Typography> <Typography sx={{ fontSize: bodyFontSize }}> {questionData.feedback_current} </Typography> </Box> </Alert> )}
                        {questionData.feedback_cumulative && ( <Alert severity="info" variant="outlined" icon={false} sx={{ mt: 1, mb: 1, py: 1, textAlign: 'left', '& .MuiAlert-message': { py: 0, width: '100%' } }}> <Box sx={{ display: 'flex', alignItems: 'flex-start' }}> <Typography sx={{ fontSize: bodyFontSize, mr: 1, flexShrink: 0 }}> <strong>Overall Trend:</strong> </Typography> <Typography sx={{ fontSize: bodyFontSize }}> {questionData.feedback_cumulative} </Typography> </Box> </Alert> )}
                        {index < reportData.multiple_choice_counts.length - 1 && <Divider sx={{ my: 2 }} />}
                    </Box>
                );
            })}
         </Paper>
    </Box>
   )}
  </Box>
 );
}

export default Report;
